import React from 'react';
import { Archive, RotateCcw, Loader2, AlertTriangle } from 'lucide-react';
import { useArchiveTuitionGrid, useRestoreTuitionGrid } from '../../hooks/useTuitionGrids';
import type { TuitionGridWithRelations } from '@/types/tuition.types';
import { formatNiveau } from '@/services/tuitionService';

interface TuitionGridArchiveModalProps {
  grid: TuitionGridWithRelations;
  onClose: () => void;
  onSuccess: () => void;
}

const TuitionGridArchiveModal: React.FC<TuitionGridArchiveModalProps> = ({
  grid,
  onClose,
  onSuccess,
}) => {
  const isArchived = !grid.isActive;
  const archiveMutation = useArchiveTuitionGrid();
  const restoreMutation = useRestoreTuitionGrid();

  const handleConfirm = async () => {
    try {
      if (isArchived) {
        await restoreMutation.mutateAsync(grid.id);
      } else {
        await archiveMutation.mutateAsync(grid.id);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Archive/Restore error:', err);
    }
  };

  const isLoading = archiveMutation.isPending || restoreMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="p-6 text-center">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isArchived ? 'bg-green-100' : 'bg-amber-100'
              }`}
            >
              {isArchived ? (
                <RotateCcw className="w-8 h-8 text-green-600" />
              ) : (
                <Archive className="w-8 h-8 text-amber-600" />
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {isArchived ? 'Restaurer la grille ?' : 'Archiver la grille ?'}
            </h3>

            <div className="text-sm text-gray-500 mb-4">
              <p className="font-medium text-gray-700">
                {grid.filiere?.name || 'Filière'} - {formatNiveau(grid.niveau)}
              </p>
              {grid.campus && (
                <p className="text-gray-400 mt-1">Campus: {grid.campus.name}</p>
              )}
            </div>

            {!isArchived && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left mb-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <p className="font-medium">Attention</p>
                    <p>
                      Cette grille tarifaire sera archivée et ne sera plus visible par défaut.
                      Vous pourrez la restaurer ultérieurement.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isArchived && (
              <p className="text-sm text-gray-500 mb-4">
                Cette grille sera restaurée et redeviendra active.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                isArchived
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isArchived ? 'Restaurer' : 'Archiver'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionGridArchiveModal;
